/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Conversations card for a lead, contact, account, deal or case.
 *
 * Voice rows expand inline recordings/transcripts. WhatsApp rows open the
 * Messages inbox for that conversation. Gates on CALL_CENTER entitlement.
 */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Card, Chip, CircularProgress, Collapse, Divider, Icon } from "@mui/material";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MonoText from "components/MonoText";
import StatusBadge from "components/StatusBadge";
import ConversationRecording from "components/CallRecording/ConversationRecording";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";
import { formatDuration } from "telephony/callStates";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import { v2 as t } from "styles/tokens/v2";

const WA_GREEN = "#25D366";

function isWhatsApp(call) {
  return (call?.channel || "").toLowerCase() === "whatsapp";
}

export default function CallHistoryCard({ recordType, recordId, limit }) {
  const navigate = useNavigate();
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const hasCallCenter = isModuleEnabled("CALL_CENTER");

  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 403 means the user has no seat for this, not that loading failed.
  const [forbidden, setForbidden] = useState(false);
  const isV2 = useTheme()?.pearlUi === "v2";
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!recordId || !hasCallCenter) return;
    setLoading(true);
    setError("");
    setForbidden(false);
    try {
      const res = await axiosInstance.get(
        `/call-console/records/${recordType}/${recordId}/calls`,
        { params: { limit } }
      );
      setCalls(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setForbidden(err?.response?.status === 403);
      setError(getApiErrorMessage(err, "Could not load conversations for this record."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, recordType, recordId, limit, hasCallCenter]);

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated) return;
    if (entitlementsLoading || !hasCallCenter) return;
    load();
  }, [isAuthReady, isAuthenticated, entitlementsLoading, hasCallCenter, load]);

  if (entitlementsLoading || !hasCallCenter) return null;

  return (
    <Card style={{ marginTop: "10px" }}>
      <MDBox
        mx={2}
        mt={-2}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <GraphicEqIcon sx={{ color: "white !important", mr: 1 }} />
          <MDTypography variant="h6" color="white">
            Conversations
          </MDTypography>
        </Box>
        {!loading && calls.length > 0 && (
          <MDTypography variant="button" color="white">
            {calls.length}
          </MDTypography>
        )}
      </MDBox>

      <MDBox p={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} color="info" />
          </Box>
        ) : error && isV2 && forbidden ? (
          <MDTypography component="p" sx={{ fontSize: 14, lineHeight: 1.5, color: t.text2 }}>
            {error}
          </MDTypography>
        ) : error ? (
          <MDTypography variant="button" color="error">
            {error}
          </MDTypography>
        ) : calls.length === 0 ? (
          <MDTypography variant="button" color="text">
            No calls or WhatsApp conversations with this record yet.
          </MDTypography>
        ) : (
          calls.map((call, index) => {
            const wa = isWhatsApp(call);
            const open = expandedId === call.id;
            const counterparty =
              call.caller_name ||
              call.contact_name ||
              (call.direction === "outbound" ? call.to_address : call.from_address);
            const when =
              call.last_message_at || call.started_at || call.answered_at || null;

            return (
              <MDBox key={call.id}>
                {index > 0 && <Divider sx={{ my: 1 }} />}
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  onClick={() => setExpandedId(open ? null : call.id)}
                  sx={{
                    cursor: "pointer",
                    py: 0.75,
                    borderRadius: "6px",
                    "&:hover": { bgcolor: "#FAFBFD" },
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    {wa ? (
                      <WhatsAppIcon sx={{ fontSize: 18, color: WA_GREEN, flexShrink: 0 }} />
                    ) : (
                      <Icon sx={{ fontSize: 18, color: "#5C6B82" }}>
                        {call.direction === "outbound" ? "call_made" : "call_received"}
                      </Icon>
                    )}
                    <MDBox sx={{ minWidth: 0 }}>
                      <MDBox display="flex" alignItems="center" gap={0.75}>
                        <MonoText variant="button">{counterparty || "Unknown number"}</MonoText>
                        {wa ? (
                          <Chip
                            size="small"
                            label="WhatsApp"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              bgcolor: WA_GREEN,
                              color: "#fff",
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        ) : null}
                      </MDBox>
                      <MDTypography variant="caption" color="text" display="block">
                        {when ? new Date(when).toLocaleString() : "Not started"}
                        {!wa && call.talk_seconds != null
                          ? ` · ${formatDuration(call.talk_seconds)}`
                          : ""}
                        {!wa && call.disposition_name ? ` · ${call.disposition_name}` : ""}
                        {wa && call.last_message_preview
                          ? ` · last message: ${call.last_message_preview}`
                          : ""}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={0.75}>
                    {!wa && call.has_recording && (
                      <Icon sx={{ fontSize: 18, color: "#5C6B82" }} title="Recording available">
                        graphic_eq
                      </Icon>
                    )}
                    {!wa && call.transcription_status === "completed" && (
                      <Icon sx={{ fontSize: 18, color: "#5C6B82" }} title="Transcript available">
                        subject
                      </Icon>
                    )}
                    {call.status && !wa && (
                      <StatusBadge status={call.status} label={call.status} size="xs" />
                    )}
                    <Icon sx={{ fontSize: 20, color: "#5C6B82" }}>
                      {open ? "expand_less" : "expand_more"}
                    </Icon>
                  </MDBox>
                </MDBox>

                <Collapse in={open} unmountOnExit>
                  <MDBox pl={3.5} pr={1} pb={1.5}>
                    {wa ? (
                      <MDBox>
                        <MDTypography variant="caption" color="text" display="block" mb={1}>
                          {call.from_address || "WhatsApp conversation"}
                          {call.last_message_preview
                            ? ` — last message: ${call.last_message_preview}`
                            : ""}
                        </MDTypography>
                        <MDButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/messages?conversation=${call.id}`);
                          }}
                        >
                          Open in Messages
                        </MDButton>
                      </MDBox>
                    ) : (
                      <ConversationRecording conversationId={call.id} />
                    )}
                  </MDBox>
                </Collapse>
              </MDBox>
            );
          })
        )}
      </MDBox>
    </Card>
  );
}

CallHistoryCard.propTypes = {
  recordType: PropTypes.oneOf(["lead", "contact", "account", "deal", "case"]).isRequired,
  recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  limit: PropTypes.number,
};

CallHistoryCard.defaultProps = {
  recordId: null,
  limit: 25,
};
